import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import './Select.css';

// Themed dropdown to replace native <select> — the browser's own dropdown
// ignores the admin theme entirely (system font, OS-native highlight color),
// which is why it looked out of place. Options can be plain strings or
// {value, label} objects.
//
// Search: a list of 8 or more options (districts, castes, cities...) opens with a search box on top. Typing narrows the list,
// the arrow keys move through it and Enter picks the highlighted one. Pass searchable={false} to switch it off, or
// searchable={true} to force it on a short list.
const normalize = opt => (typeof opt === 'string' ? { value: opt, label: opt } : opt);
const SEARCH_FROM = 8;
const DEFAULT_PAGE_SIZE = 20;

// options whose label starts with the typed text come first, then the ones that contain it anywhere
const narrow = (list, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    const starts = [], contains = [];
    list.forEach(o => {
        const label = String(o.label).toLowerCase();
        if (label.startsWith(q) || label.split(/[\s/()-]+/).some(word => word.startsWith(q))) starts.push(o);
        else if (label.includes(q)) contains.push(o);
    });
    return [...starts, ...contains];
};

const Select = ({
    value,
    onChange,
    options = [],
    placeholder = 'Select',
    disabled = false,
    className = '',
    allowEmpty = true,
    ariaLabel,
    menuMinWidth = 0,
    selectedLabel,
    searchable = 'auto',
    pageSize = DEFAULT_PAGE_SIZE,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const [page, setPage] = useState(0);
    const rootRef = useRef(null);
    const menuRef = useRef(null);
    const searchRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
    const normalized = options.map(normalize);
    const selected = normalized.find(o => o.value === value);
    const showSearch = searchable === true || (searchable === 'auto' && normalized.length >= SEARCH_FROM);
    // "Other" (to type something that is not listed) stays at the end of the list even when the search does not match it
    const narrowed = narrow(normalized, query);
    const otherOption = normalized.find(o => o.value === 'Other');
    const filtered = otherOption && query.trim() && !narrowed.includes(otherOption) ? [...narrowed, otherOption] : narrowed;
    const effectivePageSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
    const totalPages = Math.max(1, Math.ceil(filtered.length / effectivePageSize));
    const safePage = Math.min(page, totalPages - 1);
    const pageStart = safePage * effectivePageSize;
    const shown = filtered.slice(pageStart, pageStart + effectivePageSize);

    const choose = (v) => { onChange(v); setOpen(false); };

    const updatePos = React.useCallback(() => {
        if (!rootRef.current) return;
        const rect = rootRef.current.getBoundingClientRect();
        // If trigger has scrolled completely off-screen, close the dropdown
        if (rect.bottom < -150 || rect.top > window.innerHeight + 150) {
            setOpen(false);
            return;
        }
        const width = Math.max(rect.width, menuMinWidth);
        const height = menuRef.current ? menuRef.current.offsetHeight : 240;
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
        const below = rect.bottom + 6;
        const top = below + height > window.innerHeight - 8 ? Math.max(8, rect.top - height - 6) : below;
        setPos({ top, left, width });
    }, [menuMinWidth]);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = e => {
            const inside = (rootRef.current && rootRef.current.contains(e.target)) || (menuRef.current && menuRef.current.contains(e.target));
            if (!inside) setOpen(false);
        };
        const onKey = e => { if (e.key === 'Escape') setOpen(false); };

        window.addEventListener('resize', updatePos);
        window.addEventListener('scroll', updatePos, true);
        document.addEventListener('pointerdown', onDocClick);
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('resize', updatePos);
            window.removeEventListener('scroll', updatePos, true);
            document.removeEventListener('pointerdown', onDocClick);
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, updatePos]);

    // every opening starts with an empty search; only focus automatically on desktop/fine-pointer devices.
    // On touch devices (tablets/phones), focusing an input opens the software keyboard, which covers the screen
    // and causes auto-scrolling that dismisses or obstructs the options.
    useEffect(() => {
        if (!open) { setQuery(''); setActive(0); setPage(0); return; }
        const isTouch = Boolean(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
        if (!isTouch && searchRef.current) {
            searchRef.current.focus({ preventScroll: true });
        }
    }, [open]);

    // a new search starts at the first match
    useEffect(() => { setActive(0); setPage(0); }, [query]);
    useEffect(() => { setPage(current => Math.min(current, totalPages - 1)); }, [totalPages]);

    // keep the highlighted option in view while arrowing through a long list
    useEffect(() => {
        if (!open || !menuRef.current) return;
        const el = menuRef.current.querySelector('.ui-select-option.is-active');
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [active, open, query]);

    // The menu is rendered in <body> (position: fixed) so a parent with overflow:hidden can never clip it.
    useLayoutEffect(() => {
        if (!open) return;
        updatePos();
    }, [open, updatePos, options.length, shown.length]);

    // Closing on disabled toggling (e.g. state cleared while district's menu is open)
    useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

    const onSearchKey = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (active >= shown.length - 1 && safePage < totalPages - 1) { setPage(safePage + 1); setActive(0); }
            else setActive(i => Math.min(i + 1, Math.max(shown.length - 1, 0)));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (active === 0 && safePage > 0) { setPage(safePage - 1); setActive(effectivePageSize - 1); }
            else setActive(i => Math.max(i - 1, 0));
        }
        else if (e.key === 'Enter') { e.preventDefault(); if (shown[active]) choose(shown[active].value); }
    };

    const toggleOpen = () => {
        if (disabled) return;
        if (!open) {
            const selectedIndex = filtered.findIndex(option => option.value === value);
            const selectedPage = selectedIndex >= 0 ? Math.floor(selectedIndex / effectivePageSize) : 0;
            setPage(selectedPage);
            setActive(selectedIndex >= 0 ? selectedIndex % effectivePageSize : 0);
        }
        setOpen(current => !current);
    };

    return (
        <div className={`ui-select ${disabled ? 'is-disabled' : ''} ${className}`} ref={rootRef}>
            <button
                type="button"
                className={`ui-select-trigger ${open ? 'is-open' : ''}`}
                onClick={toggleOpen}
                disabled={disabled}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={selected ? 'ui-select-value' : 'ui-select-placeholder'}>
                    {selected ? (selectedLabel ?? selected.label) : placeholder}
                </span>
                <ChevronDown size={15} className="ui-select-chevron" />
            </button>
            {open && createPortal(
                <div className="ui-select-menu" role="listbox" ref={menuRef} style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, right: 'auto', margin: 0 }}>
                    {showSearch && (
                        <div className="ui-select-search">
                            <Search size={14} aria-hidden="true" />
                            <input
                                ref={searchRef}
                                type="text"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                onKeyDown={onSearchKey}
                                placeholder="Type to search"
                                aria-label={(ariaLabel || 'Options') + ' search'}
                                autoComplete="off"
                                spellCheck={false}
                            />
                        </div>
                    )}
                    {allowEmpty && !query.trim() && safePage === 0 && (
                        <button type="button" className={`ui-select-option ${!value ? 'is-selected' : ''}`}
                            onClick={() => choose('')}>
                            <span className="ui-select-option-placeholder">{placeholder}</span>
                        </button>
                    )}
                    {shown.map((o, i) => (
                        <button
                            type="button"
                            key={o.value}
                            className={`ui-select-option ${o.value === value ? 'is-selected' : ''} ${showSearch && i === active ? 'is-active' : ''}`}
                            onMouseEnter={() => showSearch && setActive(i)}
                            onClick={() => choose(o.value)}
                        >
                            {o.value === value && <Check size={13} className="ui-select-check" />}
                            <span>{o.label}</span>
                        </button>
                    ))}
                    {normalized.length === 0 && <div className="ui-select-empty">No options</div>}
                    {normalized.length > 0 && filtered.length === 0 && <div className="ui-select-empty">No match for "{query}"</div>}
                    {totalPages > 1 && (
                        <div className="ui-select-pagination" aria-label="Dropdown pages">
                            <button type="button" onClick={() => { setPage(current => Math.max(0, current - 1)); setActive(0); }} disabled={safePage === 0} aria-label="Previous options page">
                                <ChevronLeft size={15} />
                            </button>
                            <span><strong>{safePage + 1}</strong> / {totalPages}<small>{filtered.length} options</small></span>
                            <button type="button" onClick={() => { setPage(current => Math.min(totalPages - 1, current + 1)); setActive(0); }} disabled={safePage === totalPages - 1} aria-label="Next options page">
                                <ChevronRight size={15} />
                            </button>
                        </div>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
};

export default Select;
